/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiPanel, EuiText, EuiTitle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { type Budget } from '../../../types'
import ScopeItem from '../../ScopeItem'

import useCloudResourceDetails from './useCloudResourceDetails'

type Props = {
  scopeType: Budget['scope_type']
  scopeValues: Budget['scope_values']
}

const CloudResourceDetails: FunctionComponent<Props> = ({ scopeType, scopeValues }) => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const { cloudResourceLink, id, isError, isLoading, name } = useCloudResourceDetails({
    scopeValues,
  })

  const handleCloudResourceClick = () => {
    if (cloudResourceLink) {
      navigate(cloudResourceLink)
    }
  }

  const isLinkButtonDisabled = isLoading || isError || !cloudResourceLink

  return (
    <EuiFlexGroup direction='column' gutterSize='xs'>
      <EuiTitle size='xxxs'>
        <h4>
          <FormattedMessage
            id='billing.notifications.budgets.budget-details.scope.cloud-resource.title'
            defaultMessage='Cloud resource'
          />
        </h4>
      </EuiTitle>
      <EuiPanel paddingSize='m' hasBorder={true} hasShadow={false}>
        <EuiFlexGroup
          alignItems='center'
          justifyContent='spaceBetween'
          gutterSize='s'
          responsive={false}
        >
          <EuiFlexItem>
            <ScopeItem scopeValues={scopeValues} scopeType={scopeType} />
            <EuiText size='xs' color='subdued'>
              {id}
            </EuiText>
          </EuiFlexItem>
          <EuiButtonIcon
            aria-label={formatMessage(
              {
                id: 'billing.notifications.budgets.budget-details.scope.cloud-resource.view-link.aria-label',
                defaultMessage: 'Open {name} in management',
              },
              { name },
            )}
            isDisabled={isLinkButtonDisabled}
            display='empty'
            iconType='link'
            isLoading={isLoading}
            onClick={handleCloudResourceClick}
            size='s'
            type='primary'
          />
        </EuiFlexGroup>
      </EuiPanel>
    </EuiFlexGroup>
  )
}

export default CloudResourceDetails
